import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { KanbanBoard } from "../kanban/KanbanBoard";
import { NewTaskForm } from "../kanban/NewTaskForm";
import { TracksView } from "../tracks/TracksView";
import { SecurityView } from "../security/SecurityView";
import { useUI } from "../../lib/store";
import { projects, tasks } from "../../lib/api";
import { IdeasModal } from "../kanban/IdeasModal";
import { Button } from "../shared/Button";
import { cn } from "../../lib/cn";

type View = "board" | "tracks" | "security";

const VIEWS: { id: View; label: string }[] = [
  { id: "board", label: "Board" },
  { id: "tracks", label: "Tracks" },
  { id: "security", label: "Security" },
];

export function MainPane() {
  const projectId = useUI((s) => s.selectedProjectId);
  const view = useUI((s) => s.mainView);
  const setView = useUI((s) => s.setMainView);
  const [creating, setCreating] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const { data: ideas } = useQuery({
    queryKey: ["ideas", projectId],
    queryFn: () => tasks.ideas(projectId),
    refetchInterval: 60_000,
  });
  const ideaCount = ideas?.length ?? 0;

  const { data: list } = useQuery({
    queryKey: ["projects"],
    queryFn: () => projects.list(),
  });
  const project = list?.find((p) => p.id === projectId);

  return (
    <>
      <div className="px-5 pt-4 pb-3 border-b border-border-subtle bg-bg-canvas">
        <div className="flex items-start gap-4 mb-3">
          <div className="min-w-0 flex-1">
            {project ? (
              <>
                <div className="text-display text-text-primary truncate">
                  {project.name}
                </div>
                {project.description && (
                  <div className="mt-0.5 text-small text-text-secondary truncate">
                    {project.description}
                  </div>
                )}
              </>
            ) : (
              <div className="text-display text-text-primary">All projects</div>
            )}
          </div>
          <Button variant="primary" onClick={() => setCreating(true)}>
            New task
          </Button>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1" />
          {view === "board" && (
            <button
              type="button"
              onClick={() => setIdeasOpen(true)}
              className={cn(
                "inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-mono text-[11px] transition-colors",
                ideaCount > 0
                  ? "text-accent"
                  : "text-text-tertiary hover:text-text-secondary",
              )}
              style={{ background: ideaCount > 0 ? "var(--accent-soft)" : "var(--bg-inset)" }}
              title="Follow-ups and ideas waiting for your OK"
            >
              ideas:{ideaCount}
            </button>
          )}
          {view === "board" && <NeedsHumanFilter />}
          <ViewSwitch active={view} onChange={setView} />
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {view === "board" && <KanbanBoard />}
        {view === "tracks" && <TracksView />}
        {view === "security" && <SecurityView />}
      </div>

      {creating && <NewTaskForm onClose={() => setCreating(false)} />}
      {ideasOpen && <IdeasModal projectId={projectId} onClose={() => setIdeasOpen(false)} />}
    </>
  );
}

function NeedsHumanFilter() {
  const filter = useUI((s) => s.triageFilter);
  const setFilter = useUI((s) => s.setTriageFilter);
  const active = filter !== null;
  // With no filter, the chip is a quick "needs:human" toggle. When the Loops
  // tab set some other triage filter, it shows that state with an ✕ to clear.
  const label = filter ? filter.replace("_", ":") : "needs:human";
  return (
    <button
      type="button"
      onClick={() => setFilter(active ? null : "needs_human")}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-mono text-[11px] transition-colors",
        active
          ? "text-[color:var(--status-paused)]"
          : "text-text-tertiary hover:text-text-secondary",
      )}
      style={
        active
          ? { background: "var(--status-paused-soft)" }
          : { background: "var(--bg-inset)" }
      }
      title={active ? "Clear triage filter" : "Show only tasks routed to a human"}
    >
      {label}
      {active && <span aria-hidden="true">✕</span>}
    </button>
  );
}

function ViewSwitch({
  active,
  onChange,
}: {
  active: View;
  onChange: (v: View) => void;
}) {
  return (
    <div
      className="flex items-center gap-4 border-b border-border-subtle -mb-px"
      role="tablist"
      aria-label="View"
    >
      {VIEWS.map((v) => {
        const on = v.id === active;
        return (
          <button
            key={v.id}
            role="tab"
            aria-selected={on}
            onClick={() => onChange(v.id)}
            className={cn(
              "relative h-8 px-0.5 text-small bg-transparent transition-colors duration-fast ease-out",
              on
                ? "text-text-primary"
                : "text-text-tertiary hover:text-text-primary",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-canvas",
            )}
          >
            {v.label}
            {on && (
              <span
                aria-hidden="true"
                className="absolute -bottom-px left-0 right-0 h-[2px] bg-accent rounded-full"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
